// Lightweight Web Audio sound system — no external assets needed for SFX.
// SFX: synthesized soft "pop".
// Backdrop ambience: looping AAC beds (see specs/background-audio.md).
// Voice readout: browser SpeechSynthesis (zero-asset, supports every shipped language).
//
// Mixing rules (see specs/sounds.md §5) — everything below obeys these:
//  - No sound connects to ctx.destination directly; they all share one master bus with a limiter,
//    so overlapping hits can't clip. Clipping is what makes a phone's speaker protection ride the
//    whole mix down and back up, which is heard as the volume "pumping".
//  - One-shots are pooled and disconnected when they end, so a fast volley doesn't leave a growing
//    node graph on the audio render thread (WebKit reclaims finished nodes far more lazily
//    than Blink does).
//  - The ambience bed ducks smoothly under the voice readout, so the level change is ours and
//    gradual instead of iOS's abrupt one.

import type { Background } from '@/game/storage';
import { getVoiceMuted, getMusicMuted } from '@/game/storage';
import { fableVariant, type FableVariant } from '@/game/theme';
import { ttsLang } from '@/i18n';

let ctx: AudioContext | null = null;

function getCtx(): AudioContext {
  if (!ctx) {
    const Ctor = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    ctx = new Ctor();
    // Anything cached against the old context (bus, noise buffers) belongs to it, not this one.
    master = null;
    noisePools = {};
  }
  return ctx;
}

/**
 * Resume a context that isn't running, without spamming resume() — on iOS each call re-activates
 * the audio session (tens of ms), and every SFX used to fire one. Overlapping calls collapse
 * into one.
 *
 * Two WebKit details this has to survive, both of them silent-game bugs:
 *
 *  - The state to check is "not running", NOT "=== suspended". After an audio-session interruption
 *    (call, alarm, Siri) WebKit parks the context in the non-standard state 'interrupted', which is
 *    precisely the case the native nudge in AudioSession.swift exists to recover from — a
 *    suspended-only guard skipped the resume and left the game silent until relaunch. 'closed' is
 *    the one state resume() can never help with.
 *  - resume() can stay PENDING indefinitely when it is called while the session is still inactive
 *    (racing the native re-activation). The latch below is therefore released by a watchdog as well
 *    as by the promise: without it one unlucky call would wedge the latch on forever and every
 *    later attempt — including the player's own next tap — would be skipped.
 *
 * Exported for audio.test.ts; nothing outside this module calls it.
 */
let resuming = false;
/** Long enough not to fight a slow-but-real resume, short enough to be invisible between taps. */
const RESUME_WATCHDOG_MS = 1500;

export function ensureRunning(c: AudioContext): void {
  // Widened: 'interrupted' is real on WebKit but absent from the AudioContextState union.
  const state: string = c.state;
  if (state === 'running' || state === 'closed' || resuming) return;
  resuming = true;
  const watchdog = setTimeout(() => { resuming = false; }, RESUME_WATCHDOG_MS);
  const release = (): void => { clearTimeout(watchdog); resuming = false; };
  try {
    void Promise.resolve(c.resume()).catch(() => {}).finally(release);
  } catch {
    release();
  }
}

// ---------- Master bus ----------
// Every source routes through: source → master gain → limiter → destination.
// The limiter only engages on peaks (threshold −6 dBFS), so normal play is untouched; it exists to
// keep a chomp + crack + ambience pile-up from summing past full scale and clipping.

let master: GainNode | null = null;

const MASTER_GAIN = 0.9;

function getBus(): GainNode {
  const c = getCtx();
  if (!master) {
    const limiter = c.createDynamicsCompressor();
    limiter.threshold.value = -6;
    limiter.knee.value = 3;
    limiter.ratio.value = 12;
    limiter.attack.value = 0.003;
    limiter.release.value = 0.2;
    limiter.connect(c.destination);

    master = c.createGain();
    master.gain.value = MASTER_GAIN;
    master.connect(limiter);
  }
  return master;
}

/**
 * Disconnect a one-shot's whole chain once the source finishes. Without this the nodes stay wired
 * to the bus until the engine collects them, and on WebKit that is late enough that a rapid volley
 * of shots audibly loads the audio thread.
 */
function releaseOnEnd(source: AudioScheduledSourceNode, ...nodes: AudioNode[]): void {
  source.onended = (): void => {
    try { source.disconnect(); } catch { /* already gone */ }
    for (const n of nodes) {
      try { n.disconnect(); } catch { /* already gone */ }
    }
  };
}

// ---------- Noise pools ----------
// The noise-based SFX used to synthesize a fresh AudioBuffer (a per-sample JS loop) on every single
// shot, on the same thread that drives the Phaser render loop. The bursts are short and random, so
// a small pool of pre-baked variants is indistinguishable by ear and costs nothing per shot.

type NoiseKind = 'pop' | 'crack' | 'chomp' | 'frost' | 'steam';

let noisePools: Partial<Record<NoiseKind, AudioBuffer[]>> = {};

const NOISE_VARIANTS = 4;

/** A BufferSource playing a random pre-baked variant of the given noise burst. */
function noiseSource(kind: NoiseKind, duration: number, fill: (data: Float32Array) => void): AudioBufferSourceNode {
  const c = getCtx();
  let pool = noisePools[kind];
  if (!pool) {
    pool = [];
    for (let v = 0; v < NOISE_VARIANTS; v++) {
      const buffer = c.createBuffer(1, Math.floor(c.sampleRate * duration), c.sampleRate);
      fill(buffer.getChannelData(0));
      pool.push(buffer);
    }
    noisePools[kind] = pool;
  }
  const source = c.createBufferSource();
  source.buffer = pool[Math.floor(Math.random() * pool.length)];
  return source;
}

// ---------- Test/instrumentation hook ----------
// Tests (puppeteer / claude-in-chrome) read window.__audioEvents to assert that audio
// fired without depending on actually-played sound. Each entry: { kind, detail, ts }.
type AudioEvent = { kind: 'pop' | 'bg-start' | 'bg-stop' | 'speak' | 'record' | 'round' | 'star' | 'alien' | 'diamond' | 'freeze' | 'galaxy' | 'ship' | 'crack' | 'thud' | 'chomp' | 'duck' | 'journey'; detail?: string; ts: number };

function logEvent(kind: AudioEvent['kind'], detail?: string): void {
  const w = window as unknown as { __audioEvents?: AudioEvent[] };
  if (!w.__audioEvents) w.__audioEvents = [];
  w.__audioEvents.push({ kind, detail, ts: performance.now() });
  // Cap to avoid leaking memory in long sessions.
  if (w.__audioEvents.length > 500) w.__audioEvents.splice(0, w.__audioEvents.length - 500);
}

// ---------- Pop SFX ----------

/** Short soft "pop": filtered white noise with a fast exponential decay. */
export function playPopSound(): void {
  const c = getCtx();
  ensureRunning(c);

  const now = c.currentTime;
  const duration = 0.18;

  const noise = noiseSource('pop', duration, (data) => {
    for (let i = 0; i < data.length; i++) {
      data[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / data.length, 1.4);
    }
  });

  const filter = c.createBiquadFilter();
  filter.type = 'lowpass';
  filter.frequency.setValueAtTime(900, now);
  filter.frequency.exponentialRampToValueAtTime(200, now + duration);
  filter.Q.value = 1.5;

  const gain = c.createGain();
  gain.gain.setValueAtTime(0.4, now);
  gain.gain.exponentialRampToValueAtTime(0.001, now + duration);

  noise.connect(filter);
  filter.connect(gain);
  gain.connect(getBus());
  releaseOnEnd(noise, filter, gain);
  noise.start(now);
  noise.stop(now + duration);

  logEvent('pop');
}

// ---------- Crumbling Board SFX ----------

/**
 * Sharp wooden "crack" for a nail popping out of a plank (Crumbling Board). A short band-passed
 * noise burst with a fast decay — drier and higher than the soft pop, so it reads as splitting
 * wood rather than a shot. Zero assets, like the pop.
 */
export function playCrackSound(): void {
  const c = getCtx();
  ensureRunning(c);
  const now = c.currentTime;
  const duration = 0.09;

  const noise = noiseSource('crack', duration, (data) => {
    for (let i = 0; i < data.length; i++) {
      data[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / data.length, 3.0);
    }
  });

  const filter = c.createBiquadFilter();
  filter.type = 'bandpass';
  filter.frequency.setValueAtTime(2200, now);
  filter.frequency.exponentialRampToValueAtTime(1100, now + duration);
  filter.Q.value = 3;

  const gain = c.createGain();
  gain.gain.setValueAtTime(0.35, now);
  gain.gain.exponentialRampToValueAtTime(0.001, now + duration);

  noise.connect(filter);
  filter.connect(gain);
  gain.connect(getBus());
  releaseOnEnd(noise, filter, gain);
  noise.start(now);
  noise.stop(now + duration);

  logEvent('crack');
}

/**
 * Soft low "thud" for a plank tumbling off the bottom of the screen (Crumbling Board). A quick
 * low sine drop — muted, so a lost word feels like a dull landing rather than an alarm.
 */
export function playThudSound(): void {
  const c = getCtx();
  ensureRunning(c);
  const now = c.currentTime;
  const duration = 0.22;

  const osc = c.createOscillator();
  osc.type = 'sine';
  osc.frequency.setValueAtTime(180, now);
  osc.frequency.exponentialRampToValueAtTime(70, now + duration);

  const gain = c.createGain();
  gain.gain.setValueAtTime(0.0001, now);
  gain.gain.linearRampToValueAtTime(0.4, now + 0.01);
  gain.gain.exponentialRampToValueAtTime(0.001, now + duration);

  osc.connect(gain);
  gain.connect(getBus());
  releaseOnEnd(osc, gain);
  osc.start(now);
  osc.stop(now + duration);

  logEvent('thud');
}

/**
 * Crunchy "chomp" for the ladybird eating one letter (Crumbling Board §4b). Bright bandpassed noise
 * with a *granular* envelope — the noise is chopped into a handful of little grains so it crackles
 * (a smooth burst reads as a "knock"/thud instead). A downward filter sweep gives the bite its
 * shape. No low sine body — that low tone was the knock. Bites are LADYBIRD.biteMs (~400ms) apart,
 * so it can be crisp without a stream reading as machine-gun pops.
 */
export function playChompSound(): void {
  const c = getCtx();
  ensureRunning(c);
  const now = c.currentTime;
  const duration = 0.1;

  // Granular noise: white noise gated into ~14 random grains so it crackles rather than "knocks".
  const noise = noiseSource('chomp', duration, (data) => {
    const grains = 14;
    const grainLen = Math.floor(data.length / grains);
    for (let g = 0; g < grains; g++) {
      // Each grain gets its own random loudness; some are near-silent, which creates the crackle.
      const amp = Math.pow(Math.random(), 1.5);
      const start = g * grainLen;
      for (let i = 0; i < grainLen; i++) {
        const idx = start + i;
        const decay = Math.pow(1 - idx / data.length, 1.3);       // overall fade across the burst
        const grainEnv = Math.sin((Math.PI * i) / grainLen);      // soften each grain's edges
        data[idx] = (Math.random() * 2 - 1) * amp * grainEnv * decay;
      }
    }
  });

  // Bandpass keeps the crunchy mid-highs and cuts the low rumble; sweep down for the bite shape.
  const filter = c.createBiquadFilter();
  filter.type = 'bandpass';
  filter.frequency.setValueAtTime(3200, now);
  filter.frequency.exponentialRampToValueAtTime(1400, now + duration);
  filter.Q.value = 0.8;

  const gain = c.createGain();
  gain.gain.setValueAtTime(0.6, now);
  gain.gain.exponentialRampToValueAtTime(0.001, now + duration);

  noise.connect(filter);
  filter.connect(gain);
  gain.connect(getBus());
  releaseOnEnd(noise, filter, gain);
  noise.start(now);
  noise.stop(now + duration);

  logEvent('chomp');
}

// ---------- Record / achievement fanfare ----------

/**
 * Short cheerful ascending arpeggio played when the player sets a new record.
 * Synthesized triangle notes (C–E–G–C) with a sparkle tail — zero assets, like the pop SFX.
 */
export function playRecordSound(): void {
  const c = getCtx();
  ensureRunning(c);
  const now = c.currentTime;

  const bus = c.createGain();
  bus.gain.value = 0.25;
  bus.connect(getBus());

  const notes = [523.25, 659.25, 783.99, 1046.5]; // C5 E5 G5 C6
  notes.forEach((freq, i) => {
    const t = now + i * 0.11;
    const osc = c.createOscillator();
    osc.type = 'triangle';
    osc.frequency.setValueAtTime(freq, t);
    const g = c.createGain();
    g.gain.setValueAtTime(0, t);
    g.gain.linearRampToValueAtTime(0.6, t + 0.02);
    g.gain.exponentialRampToValueAtTime(0.001, t + 0.32);
    osc.connect(g);
    g.connect(bus);
    releaseOnEnd(osc, g);
    osc.start(t);
    osc.stop(t + 0.34);
  });

  // Sparkle tail on the final note.
  const tailStart = now + notes.length * 0.11;
  const sparkle = c.createOscillator();
  sparkle.type = 'sine';
  sparkle.frequency.setValueAtTime(1568, tailStart);            // G6
  sparkle.frequency.exponentialRampToValueAtTime(2093, tailStart + 0.18); // C7
  const sg = c.createGain();
  sg.gain.setValueAtTime(0, tailStart);
  sg.gain.linearRampToValueAtTime(0.4, tailStart + 0.02);
  sg.gain.exponentialRampToValueAtTime(0.001, tailStart + 0.4);
  sparkle.connect(sg);
  sg.connect(bus);
  // The sparkle is the last voice to finish, so it tears down the fanfare's own bus too.
  releaseOnEnd(sparkle, sg, bus);
  sparkle.start(tailStart);
  sparkle.stop(tailStart + 0.42);

  logEvent('record');
}

/**
 * The end-of-journey fanfare: played once when a whole lesson is mastered and CelebrationScene
 * opens. Deliberately bigger than playRecordSound — a six-note pentatonic run that resolves into a
 * held C-major chord, with two stacked sparkle sweeps over the top — because a record is a good
 * round while this is the end of a journey that took weeks. Still zero assets.
 *
 * The chord is the last voice to finish, so it tears down the fanfare's own bus.
 */
export function playJourneyFanfare(): void {
  const c = getCtx();
  ensureRunning(c);
  const now = c.currentTime;

  const bus = c.createGain();
  bus.gain.value = 0.25;
  bus.connect(getBus());

  // Ascending run — major pentatonic, so every passing interval is consonant with the chord
  // it lands on and the whole thing reads as "up, and arrived" rather than "up, and stopped".
  const run = [523.25, 587.33, 659.25, 783.99, 880.0, 1046.5]; // C5 D5 E5 G5 A5 C6
  run.forEach((freq, i) => {
    const t = now + i * 0.1;
    const osc = c.createOscillator();
    osc.type = 'triangle';
    osc.frequency.setValueAtTime(freq, t);
    const g = c.createGain();
    g.gain.setValueAtTime(0, t);
    g.gain.linearRampToValueAtTime(0.55, t + 0.02);
    g.gain.exponentialRampToValueAtTime(0.001, t + 0.3);
    osc.connect(g);
    g.connect(bus);
    releaseOnEnd(osc, g);
    osc.start(t);
    osc.stop(t + 0.32);
  });

  // The arrival: a held C major, overlapping the tail of the run so there is no gap.
  const chordStart = now + 0.62;
  const chord = [523.25, 659.25, 783.99, 1046.5]; // C5 E5 G5 C6
  chord.forEach((freq, i) => {
    const osc = c.createOscillator();
    osc.type = 'triangle';
    osc.frequency.setValueAtTime(freq, chordStart);
    const g = c.createGain();
    g.gain.setValueAtTime(0, chordStart);
    g.gain.linearRampToValueAtTime(0.22, chordStart + 0.06);
    g.gain.exponentialRampToValueAtTime(0.001, chordStart + 1.7);
    osc.connect(g);
    g.connect(bus);
    // The top voice outlasts the others by a hair, so it owns the bus teardown.
    if (i === chord.length - 1) releaseOnEnd(osc, g, bus);
    else releaseOnEnd(osc, g);
    osc.start(chordStart);
    osc.stop(chordStart + 1.72 + (i === chord.length - 1 ? 0.02 : 0));
  });

  // Two sparkle sweeps over the chord, the second starting as the first fades.
  const sparkles: [number, number, number][] = [
    [0.7, 1568, 2093],  // G6 → C7
    [1.15, 2093, 2637], // C7 → E7
  ];
  sparkles.forEach(([offset, from, to]) => {
    const t = now + offset;
    const osc = c.createOscillator();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(from, t);
    osc.frequency.exponentialRampToValueAtTime(to, t + 0.22);
    const g = c.createGain();
    g.gain.setValueAtTime(0, t);
    g.gain.linearRampToValueAtTime(0.35, t + 0.02);
    g.gain.exponentialRampToValueAtTime(0.001, t + 0.55);
    osc.connect(g);
    g.connect(bus);
    releaseOnEnd(osc, g);
    osc.start(t);
    osc.stop(t + 0.57);
  });

  logEvent('journey');
}

/**
 * Short, bright two-note rising chime played when a board (round) is cleared — a quick
 * "ding-ding" so finishing a round always has feedback. Lighter than the record fanfare
 * (playRecordSound), which is reserved for a new best time.
 */
export function playRoundCompleteSound(): void {
  const c = getCtx();
  ensureRunning(c);
  const now = c.currentTime;

  const bus = c.createGain();
  bus.gain.value = 0.22;
  bus.connect(getBus());

  // Rising major third: E6 → A6, each a quick plucky note.
  const notes = [1318.51, 1760.0];
  notes.forEach((freq, i) => {
    const t = now + i * 0.09;
    const osc = c.createOscillator();
    osc.type = 'triangle';
    osc.frequency.setValueAtTime(freq, t);
    const g = c.createGain();
    g.gain.setValueAtTime(0, t);
    g.gain.linearRampToValueAtTime(0.7, t + 0.015);
    g.gain.exponentialRampToValueAtTime(0.001, t + 0.22);
    osc.connect(g);
    g.connect(bus);
    // The second (last) note tears down the chime's own bus.
    if (i === notes.length - 1) releaseOnEnd(osc, g, bus);
    else releaseOnEnd(osc, g);
    osc.start(t);
    osc.stop(t + 0.24);
  });

  logEvent('round');
}

/**
 * The "bing" for a body lighting up on the in-game solar system (src/game/ui/PlanetStack.ts).
 *
 * Deliberately a single bright bell rather than a melody: a star can land in the middle of play,
 * so this has to register without pulling the child's attention off the board the way the
 * round-clear chime or the record fanfare does.
 */
export function playStarSound(): void {
  const c = getCtx();
  ensureRunning(c);
  const now = c.currentTime;

  const bus = c.createGain();
  bus.gain.value = 0.24;
  bus.connect(getBus());

  // Bell-ish: a fundamental plus its octave-and-a-fifth partial, struck together and rung out.
  const partials: Array<[number, number]> = [[2093.0, 0.7], [3136.0, 0.28]]; // C7 + G7
  partials.forEach(([freq, level], i) => {
    const osc = c.createOscillator();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(freq, now);
    const g = c.createGain();
    g.gain.setValueAtTime(0, now);
    g.gain.linearRampToValueAtTime(level, now + 0.008);
    g.gain.exponentialRampToValueAtTime(0.001, now + 0.55);
    osc.connect(g);
    g.connect(bus);
    // The last partial tears down the chime's own bus.
    if (i === partials.length - 1) releaseOnEnd(osc, g, bus);
    else releaseOnEnd(osc, g);
    osc.start(now);
    osc.stop(now + 0.58);
  });

  logEvent('star');
}

/**
 * The UFO settling onto Mars — the Survival run a player has just earned (UFO_AT_STACK_STARS).
 *
 * A theremin swoop, which is what a UFO sounds like to everyone who has ever seen one in a
 * cartoon: a single sine gliding up and back down with a vibrato on top. Deliberately QUIETER
 * than the star chime it follows (0.10 against 0.24) — the chime is the event, this is the little
 * tail on it, and it lands a second later while the child is already back on the board.
 */
export function playAlienSound(): void {
  const c = getCtx();
  ensureRunning(c);
  const now = c.currentTime;
  const DUR = 0.9;

  const bus = c.createGain();
  bus.gain.value = 0.1;
  bus.connect(getBus());

  const osc = c.createOscillator();
  osc.type = 'sine';
  osc.frequency.setValueAtTime(620, now);
  osc.frequency.exponentialRampToValueAtTime(1180, now + 0.3);
  osc.frequency.exponentialRampToValueAtTime(470, now + DUR);

  // The vibrato is the whole character: without it this is a slide whistle, with it a theremin.
  const lfo = c.createOscillator();
  lfo.type = 'sine';
  lfo.frequency.setValueAtTime(6.5, now);
  const lfoDepth = c.createGain();
  lfoDepth.gain.value = 26;   // ± Hz around the swept carrier
  lfo.connect(lfoDepth);
  lfoDepth.connect(osc.frequency);

  const g = c.createGain();
  g.gain.setValueAtTime(0, now);
  g.gain.linearRampToValueAtTime(0.6, now + 0.07);
  g.gain.setValueAtTime(0.6, now + DUR - 0.4);
  g.gain.exponentialRampToValueAtTime(0.001, now + DUR);

  osc.connect(g);
  g.connect(bus);
  releaseOnEnd(osc, g, bus, lfo, lfoDepth);
  osc.start(now);
  osc.stop(now + DUR + 0.02);
  lfo.start(now);
  lfo.stop(now + DUR + 0.02);

  logEvent('alien');
}

/**
 * The Diamond settling onto Saturn — the Pairs run a player has just earned
 * (DIAMOND_AT_STACK_STARS, specs/pairs-mode.md §2.3).
 *
 * Glass rather than cartoon: three struck partials in a just-intonation stack, no glide and no
 * vibrato, so it cannot be mistaken for the UFO's theremin swoop even at the same moment in the
 * same second. Kept at the alien's level (0.10) for the same reason — it is the tail on the star
 * chime, not a second event.
 */
export function playDiamondSound(): void {
  const c = getCtx();
  ensureRunning(c);
  const now = c.currentTime;

  const bus = c.createGain();
  bus.gain.value = 0.1;
  bus.connect(getBus());

  // A high, bright triad struck twice — the second strike a fifth up and a beat later, which is
  // what makes it read as a gem rather than as a bell.
  const strikes: [number, number][] = [[0, 1568], [0.13, 2349]];
  strikes.forEach(([delay, base], si) => {
    [1, 2, 3].forEach((partial, pi) => {
      const t = now + delay;
      const osc = c.createOscillator();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(base * partial, t);
      const g = c.createGain();
      const level = 0.5 / (partial * partial);   // steep rolloff: the upper partials only glint
      g.gain.setValueAtTime(0, t);
      g.gain.linearRampToValueAtTime(level, t + 0.006);
      g.gain.exponentialRampToValueAtTime(0.001, t + 0.5);
      osc.connect(g);
      g.connect(bus);
      // The very last partial of the last strike tears down the shared bus.
      if (si === strikes.length - 1 && pi === 2) releaseOnEnd(osc, g, bus);
      else releaseOnEnd(osc, g);
      osc.start(t);
      osc.stop(t + 0.55);
    });
  });

  logEvent('diamond');
}

/**
 * The Ice Maze treat's chime (specs/word-maze-mode.md §2.3) — a banked Neptune run.
 *
 * Frost rather than gem: a bright partial pair that slides UP a minor third and is dusted with a
 * short high noise shimmer, so it reads as something crystallising. It has to be distinguishable
 * from playDiamondSound at the same moment in the same second — that one is struck and static,
 * this one glides and hisses. Three treats that sound the same are one treat. Level matches the
 * alien and the diamond (0.10): it is the tail on the star chime, not a second event.
 */
export function playFreezeSound(): void {
  const c = getCtx();
  ensureRunning(c);
  const now = c.currentTime;

  const bus = c.createGain();
  bus.gain.value = 0.1;
  bus.connect(getBus());

  // Two voices a fifth apart, both gliding up a minor third over the first 200 ms.
  const voices: [number, number][] = [[0, 1046.5], [0.06, 1568]];
  voices.forEach(([delay, base], vi) => {
    const t = now + delay;
    const osc = c.createOscillator();
    osc.type = 'triangle';
    osc.frequency.setValueAtTime(base, t);
    osc.frequency.exponentialRampToValueAtTime(base * 1.19, t + 0.2);
    const g = c.createGain();
    g.gain.setValueAtTime(0, t);
    g.gain.linearRampToValueAtTime(0.42, t + 0.008);
    g.gain.exponentialRampToValueAtTime(0.001, t + 0.6);
    osc.connect(g);
    g.connect(bus);
    if (vi === voices.length - 1) releaseOnEnd(osc, g, bus);
    else releaseOnEnd(osc, g);
    osc.start(t);
    osc.stop(t + 0.65);
  });

  // The shimmer: a very short, very high noise dust over the attack — the "frost" half.
  const dust = 0.22;
  const noise = noiseSource('frost', dust, (data) => {
    for (let i = 0; i < data.length; i++) {
      data[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / data.length, 2.0);
    }
  });
  const hp = c.createBiquadFilter();
  hp.type = 'highpass';
  hp.frequency.value = 5200;
  const ng = c.createGain();
  ng.gain.setValueAtTime(0.18, now);
  ng.gain.exponentialRampToValueAtTime(0.001, now + dust);
  noise.connect(hp);
  hp.connect(ng);
  ng.connect(bus);
  releaseOnEnd(noise, hp, ng);
  noise.start(now);
  noise.stop(now + dust);

  logEvent('freeze');
}

/**
 * The Word Wagon treat's chime (specs/wagon-mode.md §2.3) — the top of the whole ladder, and the
 * biggest sound in the game because it is the last one.
 *
 * A rising swell rather than a struck note: three voices a fifth apart sliding up an octave over
 * three quarters of a second, with a long tail. The three treats below it are all short and
 * bright — the alien is a chirp, the diamond is struck, the ice glides and hisses — so the one
 * that has to be recognisable at 213 smashes is the one that takes its time. Level matches its
 * siblings (0.10): it is still the tail on the star chime, not a second event.
 */
/**
 * The 🛰️ Satellite treat — the Word Labyrinth's payout (specs/labyrinth-mode.md §2.3).
 *
 * A rising two-tone ping over a thin carrier: the noise a probe makes in every film a child has
 * seen. Deliberately unlike the alien warble, the diamond chime and the freeze shimmer — four
 * treats that sound the same are one treat — and it plays AFTER the full-stack send-off rather than
 * under it, so it is heard rather than mixed into confetti.
 */
export function playSatelliteSound(): void {
  const c = getCtx();
  ensureRunning(c);
  const now = c.currentTime;

  // The carrier: a quiet sine that holds under both pings.
  const carrier = c.createOscillator();
  const cg = c.createGain();
  carrier.type = 'sine';
  carrier.frequency.setValueAtTime(1180, now);
  cg.gain.setValueAtTime(0.0001, now);
  cg.gain.exponentialRampToValueAtTime(0.05, now + 0.06);
  cg.gain.exponentialRampToValueAtTime(0.0001, now + 0.9);
  carrier.connect(cg).connect(c.destination);
  releaseOnEnd(carrier, cg);
  carrier.start(now);
  carrier.stop(now + 0.95);

  // Two pings, the second a fifth above the first.
  for (const [at, freq] of [[0, 880], [0.16, 1320]] as [number, number][]) {
    const o = c.createOscillator();
    const g = c.createGain();
    o.type = 'triangle';
    o.frequency.setValueAtTime(freq, now + at);
    g.gain.setValueAtTime(0.0001, now + at);
    g.gain.exponentialRampToValueAtTime(0.17, now + at + 0.02);
    g.gain.exponentialRampToValueAtTime(0.0001, now + at + 0.45);
    o.connect(g).connect(c.destination);
    releaseOnEnd(o, g);
    o.start(now + at);
    o.stop(now + at + 0.5);
  }
}

/**
 * The gate of a Word Labyrinth swinging open — the moment the strip reads a sentence (§5.2).
 *
 * Warm and short. It has to read as "that was right" without being a fanfare: it fires mid-board,
 * several times a run, and the board is not finished until the child walks through.
 */
export function playGateSound(): void {
  const c = getCtx();
  ensureRunning(c);
  const now = c.currentTime;

  for (const [at, freq] of [[0, 392], [0.09, 523.25], [0.18, 659.25]] as [number, number][]) {
    const o = c.createOscillator();
    const g = c.createGain();
    o.type = 'triangle';
    o.frequency.setValueAtTime(freq, now + at);
    g.gain.setValueAtTime(0.0001, now + at);
    g.gain.exponentialRampToValueAtTime(0.14, now + at + 0.015);
    g.gain.exponentialRampToValueAtTime(0.0001, now + at + 0.34);
    o.connect(g).connect(c.destination);
    releaseOnEnd(o, g);
    o.start(now + at);
    o.stop(now + at + 0.4);
  }
}

/**
 * The 🍝 Plate treat — the Recipe Kitchen's payout (specs/recipe-mode.md §2.3).
 *
 * A little serving bell over a pan sizzle: two struck partials with a long tail, under a short band
 * of filtered noise. It is the one treat sound with a NOISE component, which is what keeps it clear
 * of the alien warble, the diamond chime, the freeze shimmer, the probe ping and the galaxy swell —
 * six treats that sound alike are one treat. Level matches its siblings (0.10 area): still the tail
 * on the star chime, not a second event.
 */
export function playPlateSound(): void {
  const c = getCtx();
  ensureRunning(c);
  const now = c.currentTime;

  // The sizzle: a short burst of noise through a high band-pass, so it reads as a pan and not as
  // static. Half a second, under everything else.
  const frames = Math.floor(c.sampleRate * 0.5);
  const buffer = c.createBuffer(1, frames, c.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < frames; i++) data[i] = (Math.random() * 2 - 1) * (1 - i / frames);
  const noise = c.createBufferSource();
  noise.buffer = buffer;
  const band = c.createBiquadFilter();
  band.type = 'bandpass';
  band.frequency.setValueAtTime(4200, now);
  band.Q.setValueAtTime(0.8, now);
  const ng = c.createGain();
  ng.gain.setValueAtTime(0.0001, now);
  ng.gain.exponentialRampToValueAtTime(0.06, now + 0.05);
  ng.gain.exponentialRampToValueAtTime(0.0001, now + 0.5);
  noise.connect(band).connect(ng).connect(c.destination);
  releaseOnEnd(noise, band, ng);
  noise.start(now);
  noise.stop(now + 0.55);

  // The bell: two partials struck together, the upper one a touch quieter, both ringing out.
  for (const [freq, level] of [[1046.5, 0.15], [1568, 0.09]] as [number, number][]) {
    const o = c.createOscillator();
    const g = c.createGain();
    o.type = 'sine';
    o.frequency.setValueAtTime(freq, now + 0.12);
    g.gain.setValueAtTime(0.0001, now + 0.12);
    g.gain.exponentialRampToValueAtTime(level, now + 0.14);
    g.gain.exponentialRampToValueAtTime(0.0001, now + 1.0);
    o.connect(g).connect(c.destination);
    releaseOnEnd(o, g);
    o.start(now + 0.12);
    o.stop(now + 1.05);
  }
}

/**
 * An ingredient dropping into the bowl (specs/recipe-mode.md §8).
 *
 * A short wet plop, PITCHED UP a semitone per ingredient already in the bowl, so a four-ingredient
 * dish plays a rising four-note figure and finishing a recipe sounds finished. That is the whole
 * reason it takes an argument: the same sound four times says "four things happened", while a rising
 * figure says "you are nearly there", which is the thing a child needs to hear mid-board.
 */
export function playIngredientSound(index = 0): void {
  const c = getCtx();
  ensureRunning(c);
  const now = c.currentTime;

  const base = 300 * Math.pow(2, Math.min(index, 6) / 12);
  const o = c.createOscillator();
  const g = c.createGain();
  o.type = 'sine';
  // The drop in pitch across the note is what makes it a plop rather than a beep.
  o.frequency.setValueAtTime(base * 2.2, now);
  o.frequency.exponentialRampToValueAtTime(base, now + 0.09);
  g.gain.setValueAtTime(0.0001, now);
  g.gain.exponentialRampToValueAtTime(0.16, now + 0.012);
  g.gain.exponentialRampToValueAtTime(0.0001, now + 0.22);
  o.connect(g).connect(c.destination);
  releaseOnEnd(o, g);
  o.start(now);
  o.stop(now + 0.25);
}

/**
 * The dish coming up on the plate — the payoff of a Recipe Kitchen round.
 *
 * A three-note rise onto a held fifth, warm and short. It fires several times a run, so it must
 * read as "that is done" without being the fanfare that ends the whole game (playRecordSound is
 * that, and it must stay bigger than this one).
 */
export function playDishSound(): void {
  const c = getCtx();
  ensureRunning(c);
  const now = c.currentTime;

  for (const [at, freq] of [[0, 523.25], [0.1, 659.25], [0.2, 783.99]] as [number, number][]) {
    const o = c.createOscillator();
    const g = c.createGain();
    o.type = 'triangle';
    o.frequency.setValueAtTime(freq, now + at);
    g.gain.setValueAtTime(0.0001, now + at);
    g.gain.exponentialRampToValueAtTime(0.15, now + at + 0.02);
    g.gain.exponentialRampToValueAtTime(0.0001, now + at + (at === 0.2 ? 0.75 : 0.3));
    o.connect(g).connect(c.destination);
    releaseOnEnd(o, g);
    o.start(now + at);
    o.stop(now + at + 0.8);
  }
}

export function playGalaxySound(): void {
  const c = getCtx();
  ensureRunning(c);
  const now = c.currentTime;

  const bus = c.createGain();
  bus.gain.value = 0.1;
  bus.connect(getBus());

  // Three voices, each entering later and higher — the pull-back, as a chord that opens out.
  const voices: [number, number][] = [[0, 261.6], [0.11, 392.0], [0.22, 523.3]];
  voices.forEach(([delay, base], vi) => {
    const t = now + delay;
    const osc = c.createOscillator();
    osc.type = 'triangle';
    osc.frequency.setValueAtTime(base, t);
    osc.frequency.exponentialRampToValueAtTime(base * 2, t + 0.75);
    const g = c.createGain();
    g.gain.setValueAtTime(0, t);
    g.gain.linearRampToValueAtTime(0.34, t + 0.06);
    g.gain.exponentialRampToValueAtTime(0.001, t + 1.5);
    osc.connect(g);
    g.connect(bus);
    if (vi === voices.length - 1) releaseOnEnd(osc, g, bus);
    else releaseOnEnd(osc, g);
    osc.start(t);
    osc.stop(t + 1.6);
  });

  logEvent('galaxy');
}

/**
 * A filled wagon rolling off (specs/wagon-mode.md §5.5) — the one moment of the mode that says
 * "that whole load was right", so it is a two-part cue rather than another pop: a short whistle
 * (two voices a fifth apart, falling away) over a rolling clack of wheels.
 *
 * Deliberately quieter and shorter than playGalaxySound: this fires every few seconds inside a
 * run, and the treat chime fires once a year.
 */
export function playShipSound(): void {
  const c = getCtx();
  ensureRunning(c);
  const now = c.currentTime;

  const bus = c.createGain();
  bus.gain.value = 0.09;
  bus.connect(getBus());

  // The whistle: a fifth, blown together and sagging as it releases.
  const voices: [number, number][] = [[0, 880], [0, 1318.5]];
  voices.forEach(([delay, base]) => {
    const t = now + delay;
    const osc = c.createOscillator();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(base, t);
    osc.frequency.exponentialRampToValueAtTime(base * 0.94, t + 0.42);
    const g = c.createGain();
    g.gain.setValueAtTime(0, t);
    g.gain.linearRampToValueAtTime(0.4, t + 0.02);
    g.gain.exponentialRampToValueAtTime(0.001, t + 0.5);
    osc.connect(g);
    g.connect(bus);
    releaseOnEnd(osc, g);   // the shared bus is released by the last wheel tick below
    osc.start(t);
    osc.stop(t + 0.55);
  });

  // The wheels: four short noise ticks, spaced a little wider each time — a wagon leaving, not a
  // wagon arriving.
  const ticks = [0.0, 0.09, 0.2, 0.34];
  ticks.forEach((offset, i) => {
    const t = now + offset;
    const dur = 0.05;
    const noise = noiseSource('steam', dur, (data) => {
      for (let n = 0; n < data.length; n++) {
        data[n] = (Math.random() * 2 - 1) * Math.pow(1 - n / data.length, 1.5);
      }
    });
    const bp = c.createBiquadFilter();
    bp.type = 'bandpass';
    bp.frequency.value = 1200;
    bp.Q.value = 0.9;
    const g = c.createGain();
    g.gain.setValueAtTime(0.3 - i * 0.05, t);
    g.gain.exponentialRampToValueAtTime(0.001, t + dur);
    noise.connect(bp);
    bp.connect(g);
    g.connect(bus);
    if (i === ticks.length - 1) releaseOnEnd(noise, bp, g, bus);
    else releaseOnEnd(noise, bp, g);
    noise.start(t);
    noise.stop(t + dur);
  });

  logEvent('ship');
}

// ---------- Backdrop ambience ----------

interface AmbienceHandle {
  bg: Background;
  /** The bed's own level control — also what the speech duck rides (see applyDuck). */
  gain: GainNode;
  /** Un-ducked level, i.e. what the duck restores to. */
  baseGain: number;
  stop: () => void;
}

let currentAmbience: AmbienceHandle | null = null;

/**
 * Start the ambience track for the given backdrop. Replaces any previous track.
 * Honours the music-mute setting (the speaker toggle in the menu): when muted, starts nothing.
 *
 * Idempotent for the *same* backdrop: if that bed is already playing it's left running, so the loop
 * continues seamlessly across the scene.restart() that happens between boards (otherwise every
 * cleared board would tear the loop down and restart it from 0 — an audible jump on device).
 */
export function startBackdropAmbience(bg: Background): void {
  if (currentAmbience && currentAmbience.bg === bg) return;
  stopBackdropAmbience();
  logEvent('bg-start', bg);
  // Log the intended backdrop above (so tests can assert it) but honour the user's mute:
  // when muted, start no bed at all.
  if (getMusicMuted()) return;
  const c = getCtx();
  ensureRunning(c);

  if (bg === 'fable') {
    currentAmbience = startFileLoop(c, 'fable', FABLE_AMBIENCE_URL[fableVariant()], AMBIENCE_GAIN);
  } else {
    const url = AMBIENCE_URL[bg];
    if (url) currentAmbience = startFileLoop(c, bg, url, AMBIENCE_GAIN);
  }
}

export function stopBackdropAmbience(): void {
  if (currentAmbience) {
    currentAmbience.stop();
    currentAmbience = null;
    logEvent('bg-stop');
  }
  // The duck rides the bed's gain node, which just went away — start the next bed unducked.
  if (duckTimer) { clearTimeout(duckTimer); duckTimer = null; }
  ducking = false;
}

// Backdrop ambience beds are generated looping AAC assets (Gemini Lyria RealTime → seamless loop →
// AAC; see specs/background-audio.md for the per-backdrop prompts). They are loudness-matched, so a
// single shared gain keeps them all at the level approved for the fable-day bed.
//
// CACHING: these files live outside /assets/**, so they are NOT content-hashed and NOT covered by
// the immutable header. Two things keep them off the wire after first play — the CacheFirst runtime
// route in vite.config.ts (web/TWA) and the /audio/** Cache-Control header in firebase.json (iOS,
// which has no service worker). Both are keyed on the path below: move these files and they go back
// to re-downloading ~100 KB on every round entry.
const AMBIENCE_GAIN = 0.32;
const AMBIENCE_URL: Partial<Record<Background, string>> = {
  nature: '/audio/background_nature.m4a',
  school: '/audio/background_school.m4a',
  space: '/audio/background_space.m4a',
  voxelworld: '/audio/background_voxelworld.m4a',
  pagoda: '/audio/background_pagoda.m4a',
};
// Fable swaps its bed with the day/night image variant (see fableVariant()).
const FABLE_AMBIENCE_URL: Record<FableVariant, string> = {
  fable_day: '/audio/background_fable_day.m4a',
  fable_night: '/audio/background_fable_night.m4a',
};

/**
 * Every bed is cut to exactly this length (specs/background-audio.md §1) — the loop window, before
 * the AAC encoder's priming samples are added. Exported so audio-assets.test.ts can check it
 * against the shipped files: a bed regenerated at a different length would silently break the
 * priming trim below.
 */
export const AMBIENCE_LOOP_SECONDS = 16;

/** An AAC-LC encoder prepends at most ~2112 priming samples; anything longer isn't priming. */
const MAX_PRIMING_SECONDS = 0.15;

/**
 * Start a seamless looping ambience from a static audio asset (fetch → decodeAudioData → looped
 * BufferSource). Returns a handle immediately; the buffer loads async and starts when ready.
 * stop() is safe to call before the load finishes (cancels the pending start) and after.
 *
 * The loop window is set explicitly rather than looping the whole buffer, because an AAC stream
 * carries encoder priming samples at the head that the container's edit list says to drop. Blink
 * honours that edit list in decodeAudioData; WebKit does not — so on iOS the decoded buffer is
 * ~32 ms longer and starts with near-silence, and looping it whole put an audible hiccup at every
 * loop point on iPhone (and only on iPhone). Measuring the overshoot against the known body length
 * recovers the true loop on both engines.
 */
function startFileLoop(c: AudioContext, bg: Background, url: string, volume: number): AmbienceHandle {
  const gain = c.createGain();
  gain.gain.value = volume;
  gain.connect(getBus());

  let stopped = false;
  let source: AudioBufferSourceNode | null = null;

  void (async () => {
    try {
      const resp = await fetch(url);
      const arr = await resp.arrayBuffer();
      const buf = await c.decodeAudioData(arr);
      if (stopped) return;
      source = c.createBufferSource();
      source.buffer = buf;
      source.loop = true;

      // Whatever the buffer runs past the known body length is un-dropped encoder priming.
      const overshoot = buf.duration - AMBIENCE_LOOP_SECONDS;
      const offset = overshoot > 0 ? Math.min(overshoot, MAX_PRIMING_SECONDS) : 0;
      if (offset > 0) {
        source.loopStart = offset;
        source.loopEnd = Math.min(offset + AMBIENCE_LOOP_SECONDS, buf.duration);
      }

      source.connect(gain);
      source.start(0, offset);
    } catch (e) {
      console.warn(`[audio] failed to load ambience asset ${url}:`, e);
    }
  })();

  return {
    bg,
    gain,
    baseGain: volume,
    stop: () => {
      stopped = true;
      if (source) { try { source.stop(); } catch { /* already stopped */ } }
      gain.disconnect();
    },
  };
}

// ---------- Speech ducking ----------
// iOS attenuates the Web Audio bed on its own whenever the system speech synthesizer takes the
// audio session, then snaps it back — heard as the ambience dipping and swelling at every board
// start (Android's TTS is out-of-process and does nothing of the sort). Ducking the bed ourselves,
// smoothly and by a known amount, means the level move is deliberate and gentle, and whatever iOS
// still does on top lands on an already-quiet bed where it barely registers.

const SPEECH_DUCK = 0.35;      // bed level while speaking, as a fraction of AMBIENCE_GAIN
const DUCK_ATTACK_SECONDS = 0.12;
const DUCK_RELEASE_SECONDS = 0.4;

/** Safety net: WebKit does not always deliver an utterance's 'end' event. */
let duckTimer: ReturnType<typeof setTimeout> | null = null;

let ducking = false;

function applyDuck(ducked: boolean): void {
  if (ducking === ducked) return;          // already there — don't re-ramp or re-log
  if (!currentAmbience || !ctx) return;
  ducking = ducked;
  const param = currentAmbience.gain.gain;
  const now = ctx.currentTime;
  const target = ducked ? currentAmbience.baseGain * SPEECH_DUCK : currentAmbience.baseGain;
  try {
    param.cancelScheduledValues(now);
    param.setValueAtTime(param.value, now);
    param.linearRampToValueAtTime(target, now + (ducked ? DUCK_ATTACK_SECONDS : DUCK_RELEASE_SECONDS));
  } catch { /* automation may throw if the node is already torn down — non-fatal. */ }
  logEvent('duck', ducked ? 'on' : 'off');
}

function duckForSpeech(fallbackMs: number): void {
  if (!currentAmbience) return;   // nothing playing to duck — don't arm a pointless timer
  applyDuck(true);
  if (duckTimer) clearTimeout(duckTimer);
  duckTimer = setTimeout(() => { duckTimer = null; applyDuck(false); }, fallbackMs);
}

function endDuck(): void {
  if (duckTimer) { clearTimeout(duckTimer); duckTimer = null; }
  applyDuck(false);
}

// ---------- Voice readout ----------

/**
 * Strip purely-visual cues from a banner before it's spoken, so TTS doesn't read out a
 * fill-in-the-blank cloze like "โต๊ะ 4 ___" as "table four underscore underscore underscore".
 * The on-screen banner is unchanged — only the spoken string is sanitized.
 *   - underscore runs (cloze blanks) → removed
 *   - bare numerals (ASCII 0-9 and Thai ๐-๙) → removed (the count is a visual prompt, and a
 *     digit is read in the wrong language when no native voice is installed)
 *   - the hyphen in "der-Wörter" → a space (see below)
 */
export function speakableText(text: string): string {
  return normalizeForSpeech(
    text
      .replace(/_+/g, ' ')
      .replace(/[0-9๐-๙]+/g, ' '),
  );
}

/**
 * Sanitize a help-panel explanation before it's spoken (see speakExplanation).
 *
 * Unlike a banner this is ordinary prose, so the two banner-only strips above are WRONG here: a
 * digit in "Nomen schreibt man gross" prose — "ab 8 Jahren", "die 3 Fälle" — is part of the
 * sentence, not a visual cue, and dropping it would read the explanation with a hole in it.
 * Newlines collapse to spaces so the wrapped panel text speaks as continuous prose.
 */
export function speakableProse(text: string): string {
  return normalizeForSpeech(text);
}

/**
 * The part both sanitizers share: quoted-article hyphens, then whitespace tidied.
 *
 * The Artikel lesson asks for "alle der-Wörter!", where "der" is the article being *quoted* —
 * the child has to hear it as its own word, because that word is the entire answer. A German
 * voice reads the hyphen as a compound join and runs the two together ("derwörter"), which
 * buries the one syllable that matters. A space restores the normal word break.
 * Deliberately only the three articles: in an ordinary compound like "Weltraum-Wörter" the
 * hyphen IS the pronunciation, and splitting it would insert a pause that does not belong.
 * Anchored to whitespace/start so a noun merely ENDING in the letters — "Bäder-Wörter" — is
 * left alone; \b would not do, since it matches between "ä" and "d".
 */
function normalizeForSpeech(text: string): string {
  return text
    .replace(/(^|\s)(der|die|das)-(?=\p{L})/giu, '$1$2 ')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Normalise a language tag before comparing — some engines report "de_DE" (underscore) or odd
 * casing, which used to defeat the exact/prefix match and wrongly silence a supported language.
 */
const normTag = (s: string): string => s.toLowerCase().replace(/_/g, '-');

/** Name markers engines use for their better voices; absent on engines that ship only one tier. */
const BETTER_VOICE_RE = /premium|enhanced|neural|siri/i;

/** Every voice whose base language is `lang`'s, in engine order — the candidate set for a readout. */
function voicesForLang(voices: SpeechSynthesisVoice[], lang: string): SpeechSynthesisVoice[] {
  const base = normTag(lang).split('-')[0];
  return voices.filter((v) => normTag(v.lang ?? '').split('-')[0] === base);
}

/** Apple's WebKit — Safari and, since every iOS browser is a WKWebView, the whole platform. */
function isAppleWebKit(): boolean {
  try {
    return typeof navigator !== 'undefined' && navigator.vendor === 'Apple Computer, Inc.';
  } catch {
    return false;
  }
}

/**
 * Should the utterance leave `voice` UNSET and let the engine resolve it from `lang` alone?
 *
 * On iOS the voice a parent picks under Settings → Accessibility → Spoken Content → Voices is
 * invisible to the Web Speech API: `getVoices()` reports exactly one entry per language (for German
 * always "Anna", the compact default), and pinning that entry is what silently overrode the chosen
 * voice. Verified on device — with `utterance.voice` left null the same phone speaks in the
 * configured voice, because WebKit then resolves through the system, which does honour the setting.
 *
 * So: where our ranking had nothing to rank — a single candidate for the language — assigning it
 * can only overrule the platform, never improve on it. Both conditions matter:
 *   - Apple WebKit only, because Android's list IS the set of installed voices and no hidden
 *     preference sits behind it; there, dropping the assignment reopens the wrong-language bug
 *     that the whole gate above exists to prevent.
 *   - a single candidate only, so macOS Safari — which does publish the full voice list — keeps
 *     the ranked pick instead of falling back to whatever the system default happens to be.
 */
function deferVoiceToPlatform(voices: SpeechSynthesisVoice[], lang: string): boolean {
  return isAppleWebKit() && voicesForLang(voices, lang).length === 1;
}

/**
 * Best installed voice for `lang`: exact tag first, else any voice of the same base language.
 * The base-language fallback is what lets "de-CH" — a tag almost no engine ships a distinct voice
 * for — speak with a "de-DE" voice instead of falling silent.
 *
 * Among equally-tagged candidates we rank rather than take the first the engine happens to list:
 * a parent who installs a better voice (iOS: Settings → Accessibility → Spoken Content → Voices)
 * should hear it, and the readout should not silently change voice just because the list order did.
 * The API exposes no quality field, so the signals are a name marker, the engine's own default
 * flag, and — as the weakest tiebreak only, since Chrome's remote "Google …" voices are often the
 * good ones — being available offline.
 *
 * Returns undefined both when the device has no such voice AND when the voice list has not loaded
 * yet; callers must distinguish those two cases via the list length (see speakRulePrompt).
 */
function findVoice(voices: SpeechSynthesisVoice[], lang: string): SpeechSynthesisVoice | undefined {
  const want = normTag(lang);
  let best: SpeechSynthesisVoice | undefined;
  let bestScore = -1;
  for (const v of voicesForLang(voices, lang)) {
    const tag = normTag(v.lang ?? '');
    let score = tag === want ? 8 : 0;
    if (BETTER_VOICE_RE.test(v.name ?? '')) score += 4;
    if (v.default) score += 2;
    if (v.localService !== false) score += 1;
    // Strict > : an engine whose voices carry none of these signals keeps today's first-wins pick.
    if (score > bestScore) { bestScore = score; best = v; }
  }
  return best;
}

/**
 * Does this device have a voice that can actually pronounce `lang`?
 *
 * Shares findVoice with speakRulePrompt on purpose: this answers "will the readout be silent?", so
 * if the two ever disagreed we would either hint at a language that speaks fine or stay mute with
 * no explanation. Used by the no-voice hint (src/ui/voiceHintOverlay.ts).
 *
 * An unloaded voice list reads as `true` — absence of evidence is not evidence of absence, and a
 * false "no voice" hint is worse than no hint.
 */
export function hasVoiceForLang(lang: string): boolean {
  if (typeof window === 'undefined' || !('speechSynthesis' in window)) return true;
  try {
    const voices = window.speechSynthesis.getVoices();
    if (voices.length === 0) return true;
    return findVoice(voices, lang) !== undefined;
  } catch {
    return true;
  }
}

/** Ambience-duck ceiling for a banner: one short sentence. */
const DUCK_CAP_MS = 12000;
/**
 * …and for a help explanation, which runs several sentences. The estimate below is ~90 ms/char, so
 * a 400-character explanation wants ~37 s; capping it at the banner's 12 s would lift the duck
 * (ambience back to full) while the voice is still mid-explanation.
 */
const DUCK_CAP_PROSE_MS = 60000;

/**
 * Speak the round prompt aloud in the given language. Returns immediately;
 * speech is async. Cancels any in-flight readout first.
 *
 * Honors abcs.voiceMute.v1; if muted, still records a 'speak' event so call sites can
 * be verified by tests, but does not actually speak.
 */
export function speakRulePrompt(text: string, lang: string, onDone?: () => void): void {
  if (!text) { onDone?.(); return; }
  speak(speakableText(text), lang, DUCK_CAP_MS, onDone);
}

/**
 * Speak a help-panel explanation aloud (HelpScene). Same engine, voice gating and mute handling as
 * the banner readout; differs only in that the text is prose, not a banner — see speakableProse for
 * what that changes, and DUCK_CAP_PROSE_MS for why the ambience has to stay ducked longer.
 *
 * Cancel it with cancelRulePrompt(); the panel does that on dismiss.
 */
export function speakExplanation(text: string, lang: string): void {
  if (!text) return;
  speak(speakableProse(text), lang, DUCK_CAP_PROSE_MS);
}

/**
 * Pronunciation repairs applied to the string handed to the ENGINE only — the on-screen text is
 * never touched. Deliberately spelling, not phonemes: SSML is not available through the Web Speech
 * API on our platforms, so the only lever we have is respelling the word the way the voice we
 * actually get would have to read it.
 *
 * "ABC" — a German voice reads the bare acronym as a syllable ("abts") rather than naming the
 * three letters, which is the one word an app called ABC Smash cannot get wrong. Separated letters
 * force the letter names, and the hyphen of "ABC-Stern" is consumed with it so the compound does
 * not glue the last letter onto the noun.
 *
 * Swiss German — virtually no device ships a de-CH voice (see findVoice), so the dialect is read
 * by a STANDARD German voice that applies German spelling rules to Swiss spellings. "uf" comes out
 * as the clipped German [ʊf] instead of the long Swiss [uːf]. Respelling it "uuf" for the engine is
 * the whole trick. Extend the table as more words turn up; keep every entry word-anchored so it
 * cannot fire inside another word.
 */
const GERMAN_SPOKEN_FIXES: ReadonlyArray<readonly [RegExp, string]> = [
  [/\bABC-?/g, 'A B C '],
  // "Tel" — the "Was fehlt?" banner shows the word stem alone ("Tel___" → "Teller"), and a German
  // voice takes a bare "Tel" for the abbreviation and reads the whole word "Telefon". That names
  // the wrong answer out loud, right when the child is choosing the syllable. "Tell" is the same
  // sounds with no abbreviation reading left.
  [/\bTel\b/g, 'Tell'],
  // "Turn" ("Turn___" → "Turnschuh"): standing alone it is also an English word, and the voice
  // switches to the English vowel ("Törn"). Doubling the "r" leaves only a German reading, with
  // the short [ʊ] the compound has in "turnen".
  [/\bTurn\b/g, 'Turrn'],
  // "Turnhal" ("Turnhal___" → "Turnhalle") reads English end to end — "Törn-hol", the vowel of
  // "hall". Same "rr", plus "ll" for the short [a] of German "Hall". The stem stops there on
  // purpose: the final "-le" is the tile, and the readout must not hand the child the answer.
  [/\bTurnhal\b/g, 'Turrnhall'],
  // The Artikel banner ("alle der-Wörter!", once the quoting hyphen is a space — see
  // normalizeForSpeech): a German voice takes the article for the unstressed function word it
  // usually is and clips it away in front of the noun, burying the one syllable the child is being
  // asked to hear. Each respelling below is the SAME sound, spelled so the voice cannot reduce it.
  // "ehr"/"ieh" are the long [eː] of "mehr" and the long [iː] of "sieh", which is the length both
  // articles have when they are said on their own. NOT "deer", which is an English word a German
  // voice may switch languages for (see "Turn" above). "das" is the odd one out: its [a] is SHORT,
  // so it cannot be lengthened without teaching the wrong word — the doubled "s" of "dass" (a real
  // German word said exactly the same) is the most that can be done, marking the vowel short so it
  // is not reduced to a schwa — and that alone was still too quick on device, so it also gets a
  // comma. The pause is the point: it puts the article phrase-final, where nothing can clip it.
  // Only "das" carries one; "der" and "die" are long enough without a break the banner never had.
  // Each is anchored to " Wörter" so an ordinary article is untouched.
  [/\bder(?= Wörter)/g, 'dehr'],
  [/\bDer(?= Wörter)/g, 'Dehr'],
  [/\bdie(?= Wörter)/g, 'dieh'],
  [/\bDie(?= Wörter)/g, 'Dieh'],
  [/\bdas(?= Wörter)/g, 'dass,'],
  [/\bDas(?= Wörter)/g, 'Dass,'],
];

const SWISS_SPOKEN_FIXES: ReadonlyArray<readonly [RegExp, string]> = [
  // "uf" (auf): German gives the bare vowel its clipped [ʊ]; the Swiss one is long.
  [/\bUf\b/g, 'Uuf'],
  [/\buf\b/g, 'uuf'],
  // "di" (dich): a two-letter word invites the clipped [dɪ]. German "ie" is the long vowel we want.
  [/\bDi\b/g, 'Die'],
  [/\bdi\b/g, 'die'],
  // "härzigi": German softens a lone "g" near "i" toward [ç] ("König"); "gg" is always the plosive.
  // A doubled "i" ("härziiggi") was tried on top of that and sounded worse — the vowel is short.
  [/\bhärzigi\b/g, 'härziggi'],
  [/\bHärzigi\b/g, 'Härziggi'],
  // "sammlisch": word-initial "s" before a vowel is voiced [z] in German and voiceless in Swiss
  // German. Doubling it is the only spelling that forces [s] this side of the word.
  [/\bsammlisch\b/g, 'ssammlisch'],
  [/\bSammlisch\b/g, 'Ssammlisch'],
  // "erreichsch": German reads the "chs" of "Fuchs"/"sechs" as [ks], turning the verb into
  // "erreiksch". The cluster has to be broken — but NOT with a separator: both "erreich-sch" and
  // "erreich sch" were tried on device, and a "sch" left standing on its own gets spelled out
  // letter by letter ("es-ce-ha"). A schwa keeps it one word: "ch" before a vowel stays [ç], and
  // the "sch" is word-internal, so it is the [ʃ] of "schwimmen". Costs a light extra syllable.
  [/\berreichsch\b/g, 'erreichesch'],
  [/\bErreichsch\b/g, 'Erreichesch'],
  // "Glace" (ice cream): a French loanword kept in Swiss spelling, said [ˈglasə] — "Glass" plus a
  // light final schwa. German rules turn the "c" before "e" into [ts] and read it "Gla-tse", which
  // is the wrong consonant, not the wrong syllable count. "Glasse" keeps both syllables and is the
  // only spelling that forces the sharp [s]: a single "s" between vowels is voiced [z] in German.
  [/\bGlace\b/g, 'Glasse'],
  [/\bglace\b/g, 'glasse'],
  // "Guetzli" (biscuit), said [ˈɡuɛtsli]: "u" and "e" are two vowels, stress on the first. In
  // German spelling "ue" is what stands in for the umlaut where no "ü" is available ("Mueller"),
  // so a German voice says "Gützli" — one vowel, and a different word to a child sounding it out.
  // "ä" is the spelling of [ɛ] that cannot be swallowed into the "u" in front of it, which keeps
  // the two vowels apart without a separator: hyphens and spaces get the chunk spelled out letter
  // by letter (see "erreichsch"). The "tz" is already the [ts] the word has, so it stays.
  [/\bGuetzli\b/g, 'Guätzli'],
  // "Buechstabe" (letter), said [ˈbuəxʃtaːbə]: the same "ue" trap as Guetzli above — a German voice
  // reads it as the umlaut stand-in and says "Büchstabe", which is a different word to a child who
  // is being told what a letter is called. Unlike Guetzli it is respelled to the STANDARD German
  // word rather than to "Buächstabe": the Swiss [uə] and the German [uː] are near neighbours, the
  // rest of the word is identical, and "Buchstabe" is a word the engine has in its dictionary — so
  // it cannot fall back to letter rules and read the "chs" as the [ks] of "Fuchs". Trading the
  // diphthong for a reading that cannot break is the right way round here.
  [/\bBuechstab/g, 'Buchstab'],
  [/\bbuechstab/g, 'buchstab'],
  // The bare article "d" (die): Swiss German reduces "die" to a single consonant that leans on the
  // noun — "d Tür" is [d̥tyːr]. A German voice sees a lone "d" standing as its own token, reads it
  // as the LETTER, and says "dö Tür". There is no respelling that glues a bare consonant onto the
  // next word for this engine: "dTür" and "d'Tür" both get spelled out.
  //
  // So it is restored to the full "die" it is a reduction of — the same trade as Buechstabe above.
  // Anchored to a following capitalised word, which is what an article precedes, so it cannot fire
  // on a stray letter elsewhere in a sentence.
  [/\bd (?=[A-ZÄÖÜ][a-zäöü])/g, 'die '],
  [/\bD (?=[A-ZÄÖÜ][a-zäöü])/g, 'Die '],
  [/\bguetzli\b/g, 'guätzli'],
];

function spokenForm(text: string, lang: string): string {
  const tag = normTag(lang);
  if (tag.split('-')[0] !== 'de') return text;
  let out = text;
  for (const [re, to] of GERMAN_SPOKEN_FIXES) out = out.replace(re, to);
  if (tag === 'de-ch') for (const [re, to] of SWISS_SPOKEN_FIXES) out = out.replace(re, to);
  return out.replace(/\s+/g, ' ').trim();
}

/** Shared body of the readouts above: `raw` is already sanitized by the caller. */
/**
 * The utterance currently being spoken, held so the garbage collector cannot take it mid-word.
 * See the note where it is assigned — this reference is load-bearing, not bookkeeping.
 */
let speaking: SpeechSynthesisUtterance | null = null;

function speak(raw: string, lang: string, duckCapMs: number, onDone?: () => void): void {
  // Fires exactly once, on EVERY route out of this function — including the four silent ones
  // (empty text, muted, no engine, no voice for the language) and the throw. A caller that waits
  // for the readout to finish must not be left waiting when there was never a readout to finish;
  // silence is a normal outcome here, not an error (see the no-voice note below).
  let settled = false;
  const done = (): void => {
    if (settled) return;
    settled = true;
    onDone?.();
  };

  const spoken = spokenForm(raw, lang);
  logEvent('speak', `${lang}:${spoken}`);
  if (!spoken) { done(); return; }
  if (getVoiceMuted()) { done(); return; }
  if (typeof window === 'undefined' || !('speechSynthesis' in window)) { done(); return; }

  try {
    const voices = window.speechSynthesis.getVoices();
    const match = findVoice(voices, lang);
    // No voice for this language → stay SILENT. Never read a word with another language's voice.
    //
    // This app teaches children to read, so the readout is the pronunciation model: German words
    // spoken by an English voice don't just sound wrong, they teach the wrong sounds. That makes
    // silence the safe failure, for every language and not only the non-Latin scripts where the
    // output is obvious gibberish.
    //
    // Do NOT "restore best effort" here — that was fe2656b's fix for what was really a *matching*
    // bug (engines reporting "de_DE" defeated the comparison), since fixed by normTag above and
    // pinned by audio.test.ts. With matching robust, an absent voice is genuinely absent, and the
    // player is told about it once instead of being left to wonder (src/ui/voiceHintOverlay.ts).
    //
    // An EMPTY list is different: it means the engine has not published its voices yet, not that
    // none exist. Speak best-effort there or the first board of a session would be mute on Android.
    if (voices.length > 0 && !match) {
      logEvent('speak', `${lang}:skip-no-voice`);
      done();
      return;
    }
    // Android/Chrome can leave the engine in a paused state after a prior cancel; a resume()
    // before speaking is a harmless no-op when nothing is paused. (Optional-chained so the
    // node test stub, which has no resume(), is unaffected.)
    window.speechSynthesis.resume?.();
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(spoken);
    // HELD until it finishes, and that is not tidiness — it is the fix for words losing their last
    // syllable. Chrome keeps no strong reference to a speaking utterance, so once the only one left
    // is a local that has gone out of scope, the object can be collected mid-sentence and the voice
    // simply stops where it was. It clips the END, it is intermittent, and it gets worse the busier
    // the frame is, which is exactly how it was reported ("swallows sometimes the last syllable").
    //
    // BoardScene worked around the same symptom by lengthening a hold so a cancel would not arrive
    // mid-word; that was a different cause with the same shape, and neither fix covers the other.
    speaking = u;
    // Region-qualified ("de" → "de-DE"): a bare tag makes Android's engine fall back to the system
    // default voice. Matters most on the empty-list path above, where u.lang is the ONLY steer the
    // engine gets. See TTS_LANG in src/i18n/index.ts.
    u.lang = ttsLang(lang);
    u.rate = 0.9;
    if (match && deferVoiceToPlatform(voices, lang)) {
      // Hand over the language, not the voice (see deferVoiceToPlatform). Speak the tag the matched
      // voice reports rather than ours: for de-CH the only candidate is a de-DE voice, and an
      // unpinned "de-CH" utterance is one iOS may not resolve at all — which would drop the readout
      // back onto the system-language voice and read German words in, say, English.
      // Underscores repaired ("de_DE"), case left alone — BCP-47 is case-insensitive, and handing
      // the engine back the exact tag it reported is one less thing for it to re-interpret.
      u.lang = (match.lang ?? '').replace(/_/g, '-') || u.lang;
    } else if (match) {
      u.voice = match;
    }
    // Duck the bed for the readout. 'start' fires when the engine actually begins (the duck is
    // already on by then — re-applying is harmless); 'end'/'error' lift it, and the timer covers
    // the WebKit case where neither ever arrives. ~90 ms/char at rate 0.9 is a generous estimate.
    u.onstart = (): void => applyDuck(true);
    // Released only here: while `speaking` points at it the collector cannot take it, and both
    // routes out of an utterance clear it, so a run of readouts holds one at a time rather than
    // leaking every word the game has ever spoken.
    u.onend = (): void => { if (speaking === u) speaking = null; endDuck(); done(); };
    u.onerror = (): void => { if (speaking === u) speaking = null; endDuck(); done(); };
    window.speechSynthesis.speak(u);
    duckForSpeech(Math.min(duckCapMs, 1500 + spoken.length * 90));
  } catch {
    /* speech APIs may throw in headless environments — non-fatal. */
    endDuck();
    done();
  }
}

/** Cancel any in-progress readout. Call when the player interacts mid-speech. */
export function cancelRulePrompt(): void {
  endDuck();
  if (typeof window === 'undefined' || !('speechSynthesis' in window)) return;
  try {
    const synth = window.speechSynthesis;
    // BoardScene calls this on *every* tap. cancel() is not free on iOS — it crosses into the
    // speech service — so skip it when there is nothing to cancel, which is the common case.
    if (!synth.speaking && !synth.pending) return;
    synth.cancel();
    // Not every engine fires onend after a cancel, and a reference left dangling here would hold
    // the last utterance of the session alive for no reason.
    speaking = null;
  } catch { /* */ }
}

// ---------- Mobile speech/audio unlock ----------
// Mobile browsers gate SpeechSynthesis behind a user gesture and populate getVoices()
// asynchronously. On a phone the very first board's readout would otherwise either be blocked
// (no gesture yet) or make the wrong match/skip decision (voice list still empty). We prime both
// on the first interaction — long before the first board reads — so by the time speakRulePrompt
// runs the engine is unlocked and the voice list is loaded.

let speechUnlocked = false;

function primeSpeech(): void {
  if (speechUnlocked) return;
  if (typeof window === 'undefined' || !('speechSynthesis' in window)) return;
  speechUnlocked = true;
  try {
    const synth = window.speechSynthesis;
    // Touch the voice list now, and keep it fresh when the engine finishes loading voices.
    synth.getVoices();
    synth.addEventListener?.('voiceschanged', () => { try { synth.getVoices(); } catch { /* */ } });
    // A silent utterance unlocks the engine within this user gesture without audible output.
    const u = new SpeechSynthesisUtterance(' ');
    u.volume = 0;
    synth.speak(u);
    synth.resume?.();
  } catch { /* speech APIs may throw in headless environments — non-fatal. */ }
}

/**
 * Attach boot-time audio lifecycle listeners (call once, see main.ts):
 *  - Any user gesture: unlock the Web Audio context (SFX/ambience) and SpeechSynthesis, which
 *    both start suspended on mobile until a gesture, so gameplay audio "just works".
 *  - App backgrounded (tab hidden / phone home button / app switcher): suspend ALL audio so the
 *    ambience doesn't keep playing when the app isn't visible; resume when it becomes visible again.
 */
export function initAudioUnlock(): void {
  if (typeof window === 'undefined') return;

  const events = ['pointerdown', 'touchstart', 'keydown'] as const;
  // These stay bound for the life of the app rather than detaching after the first gesture: iOS
  // suspends the context again after any audio-session interruption (a call, an alarm, another app
  // taking the session), and without a live listener the game stayed silent until the next reload.
  // Both handlers are guarded no-ops once primed and running, so leaving them attached is free.
  const onGesture = (): void => {
    primeSpeech();
    if (ctx) ensureRunning(ctx);
  };
  events.forEach((ev) => window.addEventListener(ev, onGesture, { passive: true, capture: true }));

  // Native hook for the iOS wrapper: it calls this after re-activating the audio session following
  // an interruption (call, alarm, headphones unplugged — see AudioSession.swift), because WebKit
  // leaves the context suspended and there may be no tap coming to revive it.
  (window as unknown as { __abcsResumeAudio?: () => void }).__abcsResumeAudio = (): void => {
    if (ctx) ensureRunning(ctx);
  };

  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        // Pause all output while backgrounded. Suspending the shared context freezes the ambience
        // (and any SFX) in place; also cut any in-flight readout.
        try { if (ctx && ctx.state === 'running') void ctx.suspend(); } catch { /* */ }
        cancelRulePrompt();
      } else {
        // Back in the foreground — un-pause. (No-op if never created or already running; the
        // ambience source keeps its loop position across the suspend.)
        if (ctx) ensureRunning(ctx);
      }
    });
  }
}
